/**
 * Handles venue submission/revision form data and API calls
 *
 * @author Matt Kenefick <matt@polymermallard.com>
 * @package Service
 * @project ChalkySticks App
 */
export class VenueRevision {
	/**
	 * @type any
	 */
	private alert: any;

	/**
	 * @type any
	 */
	private store: any;

	/**
	 * @param any vueInstance
	 */
	constructor(vueInstance: any) {
		this.alert = vueInstance.$alert;
		this.store = vueInstance.$store;
	}

	/**
	 * Submits form data with image upload support
	 *
	 * @param ISubmissionData data
	 * @param ISubmissionConfig config
	 * @return Promise<boolean>
	 */
	public async submit(data: ISubmissionData, config: ISubmissionConfig): Promise<boolean> {
		const formData = new FormData();

		formData.append('notes', data.notes || '');

		if (data.additionalData) {
			formData.append('data', JSON.stringify(data.additionalData));
		}

		if (data.imageFile) {
			formData.append('image', data.imageFile);
		}

		try {
			const token = this.store.getters['authentication/token'];
			const response = await fetch(config.endpoint, {
				body: formData,
				headers: { Authorization: 'Bearer ' + token },
				method: 'POST',
			});

			if (!response.ok) {
				let serverMessage: string | null = null;

				try {
					const responseData = await response.json();
					serverMessage = responseData.message || responseData.error;
				} catch {
					// If response isn't JSON or doesn't have a message, use default handling
				}

				this.handleError(response.status, config.onError, serverMessage || undefined);
				return false;
			}

			if (config.onSuccess) {
				config.onSuccess();
			}

			return true;
		} catch (error) {
			this.alert.alert('An error occurred while submitting.');
			return false;
		}
	}

	/**
	 * Handles submission errors
	 *
	 * @param number status
	 * @param Function customHandler
	 * @param string serverMessage
	 * @return void
	 */
	private handleError(
		status: number,
		customHandler?: (status: number, serverMessage?: string) => void,
		serverMessage?: string,
	): void {
		if (customHandler) {
			customHandler(status, serverMessage);
			return;
		}

		// Use server message if available, otherwise use default messages
		if (serverMessage) {
			this.alert.alert(serverMessage);
			return;
		}

		const messages: Record<number, string> = {
			400: 'Please provide some content.',
			401: 'You must be logged in.',
			403: 'You are not allowed to submit.',
			409: 'A similar submission already exists. Thanks!',
			422: 'Missing required information.',
		};

		const message = messages[status] || 'An error occurred while submitting.';
		this.alert.alert(message);
	}
}
